/**
 * Calculate the cost of token usage based on per-1M-token rates.
 * Returns null when rate data is unavailable so callers can distinguish
 * "no cost info" from an actual zero-dollar cost.
 * 
 * @param {number} inputTokens - Number of input tokens
 * @param {number} outputTokens - Number of output tokens
 * @param {number|null} costPer1MInput - Cost per 1 million input tokens (e.g., 3.00)
 * @param {number|null} costPer1MOutput - Cost per 1 million output tokens (e.g., 15.00)
 * @returns {number|null} - The calculated cost in USD, or null if rates are unavailable
 */
export const calculateCost = (inputTokens, outputTokens, costPer1MInput, costPer1MOutput) => {
  if (costPer1MInput == null && costPer1MOutput == null) {
    return null;
  }

  const inputCost = (inputTokens || 0) * (costPer1MInput || 0) / 1_000_000;
  const outputCost = (outputTokens || 0) * (costPer1MOutput || 0) / 1_000_000;

  const cost = inputCost + outputCost;

  // Return cost with 4 decimal places
  return parseFloat(cost.toFixed(4));
};

/**
 * Sum multiple cost values. Returns null if all values are null (no rate data
 * available for any model), otherwise sums the numeric values and treats nulls as 0.
 *
 * @param {...(number|null)} costs - Cost values to sum
 * @returns {number|null} - The total cost, or null if no rate data exists
 */
export const sumCosts = (...costs) => {
  const validCosts = costs.filter(c => c != null);
  if (validCosts.length === 0) return null;
  return parseFloat(validCosts.reduce((sum, c) => sum + c, 0).toFixed(4));
};

/**
 * Format a cost value as USD currency
 * 
 * @param {number} cost - The cost value
 * @returns {string} - Formatted cost string
 */
export const formatCost = (cost) => {
  if (cost === null || cost === undefined) return 'N/A';
  return `$${cost.toFixed(4)}`;
};